var __jsx = React.createElement;
import React, { useEffect, useRef } from "react";
import AssistantMessage from "./AssistantMessage";
import Header from "./Header";
import { useOrqChat } from "./useOrqChat";
import { useOrqTransport } from "./useOrqTransport";
import ChatInput from "../ChatInput";
import CustomUserMessage from "../CustomUserMessage";
import DraggableModal from "../DraggableModal";

/**
 * Aira chat backed by orqestrator, rendered instead of CopilotSidebar when
 * NEXT_PUBLIC_ORQ_CHAT_ENABLED is on.
 *
 * Rendering only: conversation state lives in useOrqChat, and the call to
 * orqestrator in useOrqTransport. Reuses Header plus
 * DraggableModal, ChatInput and CustomUserMessage unchanged, so the window
 * behaves exactly as it does today: dragging, minimise, the mobile full-screen
 * variant, and the existing Menubar button that opens it.
 */
var Sidebar = function Sidebar() {
  var endRef = useRef(null);
  var _useOrqTransport = useOrqTransport(),
    transport = _useOrqTransport.transport,
    loadHistory = _useOrqTransport.loadHistory,
    newThread = _useOrqTransport.newThread,
    threadScope = _useOrqTransport.threadScope;
  var _useOrqChat = useOrqChat(transport, loadHistory, threadScope),
    messages = _useOrqChat.messages,
    status = _useOrqChat.status,
    send = _useOrqChat.send,
    clear = _useOrqChat.clear;

  // Follow the reply as it streams, otherwise text runs off the bottom and the
  // user has to scroll by hand while it is still being written. Depends on
  // `status` too, so the last delta of a turn is scrolled into view.
  useEffect(function () {
    var _endRef$current;
    (_endRef$current = endRef.current) === null || _endRef$current === void 0 || _endRef$current.scrollIntoView({
      behavior: "smooth"
    });
  }, [messages, status]);

  // New Chat has to forget both halves: the messages on screen, and the
  // conversation id orqestrator is keeping history against.
  var handleNewChat = function handleNewChat() {
    clear();
    newThread();
  };
  return (
    /*#__PURE__*/
    // WindowProps requires these three, though DraggableModal itself only reads
    // `children`. Passed explicitly rather than cast, so a future DraggableModal
    // that does honour them gets values that match how this window behaves: it
    // closes via the header button only.
    React.createElement(DraggableModal, {
      clickOutsideToClose: false,
      hitEscapeToClose: false,
      shortcut: ""
    }, /*#__PURE__*/React.createElement(Header, {
      title: "AI Assistant",
      hasMessages: messages.length > 0,
      onNewChat: handleNewChat
    }), /*#__PURE__*/React.createElement("div", {
      className: "flex-1 overflow-y-auto px-4 py-4 text-left"
    }, messages.length === 0 ? /*#__PURE__*/React.createElement("p", {
      className: "text-sm text-gray-500 dark:text-gray-400"
    }, "Ask Qure AI Assistant anything to get started.") : messages.map(function (message) {
      return message.role === "user" ? /*#__PURE__*/React.createElement(CustomUserMessage, {
        key: message.id,
        message: message.content,
        rawData: null
      }) : /*#__PURE__*/React.createElement(AssistantMessage, {
        key: message.id,
        content: message.content,
        streaming: message.streaming,
        failed: message.failed,
        tools: message.tools
      });
    }), /*#__PURE__*/React.createElement("div", {
      ref: endRef
    })), /*#__PURE__*/React.createElement(ChatInput, {
      onSend: send,
      inProgress: status === "streaming",
      placeholder: "Ask Qure AI Assistant..."
    }))
  );
};
export default Sidebar;